import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Gavel, Car, Search, Handshake, ArrowRight, Phone, CheckCircle,
  DollarSign, Shield, FileText, TrendingUp, Clock, Users,
  ChevronDown, ChevronUp, Calculator, MapPin
} from 'lucide-react';

const QUIZ_QUESTIONS = [
  { q: 'What will this vehicle primarily be used for?', options: ['Personal daily driver', 'Business / rideshare income', 'Special occasions / weekend', 'Investment / collection'] },
  { q: 'What is your target budget range?', options: ['Under $20,000', '$20,000 – $40,000', '$40,000 – $80,000', '$80,000+'] },
  { q: 'How quickly do you need a vehicle?', options: ['Within 2 weeks', '1-2 months', 'No rush — finding the right one', 'Exploring options'] },
  { q: 'What matters most to you?', options: ['Lowest price possible', 'Reliability & low maintenance', 'Performance & prestige', 'Income potential / ROI'] },
  { q: 'How would you prefer to pay?', options: ['Cash / full payment', 'Traditional financing', 'Monthly payments (lease to own)', 'Need guidance on options'] },
];

const QUIZ_RESULTS = {
  auction: { title: 'Dealer Auction Access', desc: 'Based on your answers, you\'re a great fit for ACE\'s auction sourcing. We can find quality vehicles at wholesale prices — often $3,000–$8,000 below retail.', color: 'emerald', icon: Gavel, cta: 'Start Auction Search' },
  dealership: { title: 'Dealership Consulting', desc: 'ACE will prepare you with market intelligence, negotiation strategy, and financing pre-checks before you visit any dealership — so you walk in with confidence.', color: 'amber', icon: Handshake, cta: 'Book Consulting Session' },
  leaseToOwn: { title: 'Lease to Own Program', desc: 'ACE\'s lease-to-own path lets you drive and earn now while building equity toward full ownership. Ideal for your timeline and payment preference.', color: 'sky', icon: TrendingUp, cta: 'Explore Lease to Own' },
};

const SalesInfo = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [leaseCalc, setLeaseCalc] = useState({ price: 35000, down: 5000, months: 36, rate: 5.9 });
  const [quizStep, setQuizStep] = useState(-1);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);

  const computeQuizResult = (answers) => {
    // Simple scoring: map answers to recommended path
    let auctionScore = 0, dealerScore = 0, leaseScore = 0;
    // Q1: Use case
    if (answers[0] === 0 || answers[0] === 1) auctionScore += 2;
    if (answers[0] === 2) dealerScore += 2;
    if (answers[0] === 3) auctionScore += 1;
    // Q2: Budget
    if (answers[1] <= 1) auctionScore += 2;
    if (answers[1] === 2) dealerScore += 2;
    if (answers[1] === 3) dealerScore += 1;
    // Q3: Timeline
    if (answers[2] === 0) dealerScore += 2;
    if (answers[2] === 1) auctionScore += 2;
    if (answers[2] >= 2) auctionScore += 1;
    // Q4: Priority
    if (answers[3] === 0) auctionScore += 3;
    if (answers[3] === 1) dealerScore += 1;
    if (answers[3] === 2) dealerScore += 2;
    if (answers[3] === 3) leaseScore += 3;
    // Q5: Payment
    if (answers[4] === 0) auctionScore += 2;
    if (answers[4] === 1) dealerScore += 2;
    if (answers[4] >= 2) leaseScore += 3;

    if (leaseScore >= auctionScore && leaseScore >= dealerScore) return QUIZ_RESULTS.leaseToOwn;
    if (auctionScore >= dealerScore) return QUIZ_RESULTS.auction;
    return QUIZ_RESULTS.dealership;
  };

  const handleQuizAnswer = (answerIdx) => {
    const newAnswers = [...quizAnswers, answerIdx];
    setQuizAnswers(newAnswers);
    if (quizStep < QUIZ_QUESTIONS.length - 1) {
      setQuizStep(quizStep + 1);
    } else {
      setQuizResult(computeQuizResult(newAnswers));
    }
  };

  const monthlyPayment = (() => {
    const principal = leaseCalc.price - leaseCalc.down;
    const r = leaseCalc.rate / 100 / 12;
    if (r === 0) return Math.round(principal / leaseCalc.months);
    return Math.round((principal * r * Math.pow(1 + r, leaseCalc.months)) / (Math.pow(1 + r, leaseCalc.months) - 1));
  })();

  const FAQS = [
    { q: 'What types of vehicles can ACE source?', a: 'Everything from daily drivers and luxury sedans to exotics, trucks, and commercial fleet vehicles. We have access to dealer-only auctions, off-lease inventory, and private seller networks across the country.' },
    { q: 'How does the auction process work?', a: 'ACE has dealer-level access through Auctionwell and other platforms. We review available inventory, provide condition reports and market comparisons, then bid on your behalf. You never need to attend — we handle everything from bidding to transport.' },
    { q: 'What does dealership consulting include?', a: 'Before you step foot on a lot, ACE prepares you with market pricing data, negotiation strategy, financing pre-approval guidance, and a clear target price. We can also accompany you virtually or in person.' },
    { q: 'How does Lease to Own work?', a: 'ACE structures a path from renting to owning. You make a down payment, then monthly payments that build equity. At the end of the term, the vehicle is yours. This is ideal for entrepreneurs building business credit while using the vehicle for income.' },
    { q: 'Is financing available?', a: 'ACE does not provide direct financing, but we coordinate with our lending network to help you secure the best terms. We also offer business credit consulting to improve your approval odds.' },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="sales-info-page">

      {/* Hero */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <p className="text-emerald-500 text-xs font-bold tracking-[0.3em] uppercase mb-6">ACE Vehicle Acquisition</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6">
            Your Next Vehicle.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-500">Our Expertise.</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            From dealer auctions to dealership negotiations to lease-to-own programs — ACE handles the entire vehicle acquisition process so you get the right vehicle at the right price.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/consultation/intake')} className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8 text-sm font-bold" data-testid="sales-cta-start">
              <MapPin className="h-4 w-4 mr-2" /> Start Your Vehicle Search
            </Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-12 px-6 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold transition-colors">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>
        </div>
      </section>

      {/* Vehicle Finder Quiz */}
      <section className="py-16 border-t border-gray-900 bg-gray-950/40" data-testid="vehicle-finder-quiz">
        <div className="max-w-2xl mx-auto px-6">
          {quizStep === -1 && !quizResult && (
            <div className="text-center">
              <Search className="h-8 w-8 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Vehicle Finder</h2>
              <p className="text-sm text-gray-500 mb-6">Answer 5 quick questions and ACE will recommend the best acquisition path for you.</p>
              <Button onClick={() => setQuizStep(0)} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-8 text-sm font-bold" data-testid="start-quiz-btn">
                Start Quiz <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}

          {quizStep >= 0 && !quizResult && (
            <div data-testid="quiz-question">
              <div className="flex items-center justify-between mb-6">
                <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest">Question {quizStep + 1} of {QUIZ_QUESTIONS.length}</p>
                <div className="flex gap-1">
                  {QUIZ_QUESTIONS.map((_, i) => (
                    <div key={i} className={`w-8 h-1.5 rounded-full ${i <= quizStep ? 'bg-emerald-500' : 'bg-gray-800'}`} />
                  ))}
                </div>
              </div>
              <h3 className="text-lg font-bold mb-6">{QUIZ_QUESTIONS[quizStep].q}</h3>
              <div className="space-y-3">
                {QUIZ_QUESTIONS[quizStep].options.map((opt, i) => (
                  <button key={i} onClick={() => handleQuizAnswer(i)}
                    className="w-full text-left p-4 rounded-xl border border-gray-800 bg-black hover:border-emerald-600 hover:bg-gray-950 transition-all text-sm font-medium text-gray-300"
                    data-testid={`quiz-option-${i}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {quizResult && (
            <div className="text-center" data-testid="quiz-result">
              <quizResult.icon className={`h-10 w-10 text-${quizResult.color}-500 mx-auto mb-4`} />
              <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest mb-2">Your Recommended Path</p>
              <h3 className="text-2xl font-bold mb-3">{quizResult.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-6 max-w-lg mx-auto">{quizResult.desc}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/consultation/intake')} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-6 text-sm font-bold" data-testid="quiz-cta">
                  {quizResult.cta} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button onClick={() => { setQuizStep(-1); setQuizAnswers([]); setQuizResult(null); }} variant="outline" className="border-gray-700 text-gray-400 h-11 px-6 text-sm" data-testid="quiz-retake">
                  Retake Quiz
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Auctionwell — Dealer Auction Access */}
      <section className="py-20 border-t border-gray-900" data-testid="section-auctionwell">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                  <Gavel className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-[10px] text-emerald-500 font-bold tracking-widest uppercase">Dealer Access</p>
                  <h2 className="text-2xl sm:text-3xl font-bold">Auctionwell</h2>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                Most people don't know that dealers buy their best inventory at wholesale auctions — closed to the public. ACE has dealer-level access through Auctionwell and other auction platforms, giving you access to thousands of vehicles at below-market prices.
              </p>
              <p className="text-gray-400 leading-relaxed mb-6">
                We handle the entire process: searching inventory, reviewing condition reports, running vehicle history, setting bid limits, and coordinating transport. You get dealer pricing without being a dealer.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Search, label: 'Inventory Search', desc: 'Nationwide auction access' },
                  { icon: FileText, label: 'Condition Reports', desc: 'Full vehicle inspection data' },
                  { icon: Shield, label: 'History Verified', desc: 'CarFax & title checks' },
                  { icon: TrendingUp, label: 'Below Market', desc: 'Wholesale dealer pricing' },
                ].map((f, i) => (
                  <div key={i} className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <f.icon className="h-5 w-5 text-emerald-500 mb-2" />
                    <p className="text-sm font-bold">{f.label}</p>
                    <p className="text-[10px] text-gray-500">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <Card className="bg-gray-950 border-gray-800 p-6 text-white">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Gavel className="h-5 w-5 text-emerald-500" /> How Auction Access Works
              </h3>
              <div className="space-y-4">
                {[
                  { step: '01', title: 'Discovery Call', desc: 'Tell ACE what you need — make, model, year range, budget, and intended use.' },
                  { step: '02', title: 'Auction Search', desc: 'We search live auction inventory across Auctionwell and partner platforms nationwide.' },
                  { step: '03', title: 'Vehicle Review', desc: 'ACE reviews condition reports, runs vehicle history, and compares market pricing.' },
                  { step: '04', title: 'Strategic Bidding', desc: 'We set bid limits based on your budget and market value, then bid on your behalf.' },
                  { step: '05', title: 'Transport & Delivery', desc: 'Won vehicles are inspected, transported, and delivered to your door.' },
                ].map((s, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="text-emerald-500 font-black text-lg shrink-0 w-8">{s.step}</span>
                    <div>
                      <p className="text-sm font-bold">{s.title}</p>
                      <p className="text-xs text-gray-500">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Choosing the Right Vehicle */}
      <section className="py-20 border-t border-gray-900 bg-gray-950/30" data-testid="section-choose-vehicle">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-500 text-xs font-bold tracking-[0.3em] uppercase mb-3">Vehicle Selection</p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Choosing the Right Vehicle for Your Needs</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">ACE doesn't just find cars — we match you with the right vehicle based on your lifestyle, budget, and goals.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Users, title: 'Personal Use',
                desc: 'Daily drivers, family vehicles, weekend cars. We help you find the perfect balance of comfort, reliability, and value.',
                points: ['Lifestyle assessment', 'Budget optimization', 'Insurance considerations', 'Maintenance cost analysis'],
              },
              {
                icon: Car, title: 'Business / Fleet',
                desc: 'Revenue-generating vehicles for rideshare, delivery, or black car service. We focus on ROI, durability, and tax advantages.',
                points: ['Revenue projection', 'Fleet scaling strategy', 'Commercial insurance guidance', 'Tax deduction planning'],
              },
              {
                icon: TrendingUp, title: 'Investment / Exotic',
                desc: 'Vehicles that appreciate or generate premium rental income. ACE identifies high-value acquisitions with strong market positioning.',
                points: ['Market value tracking', 'Appreciation potential', 'Rental income projection', 'Collector market access'],
              },
            ].map((item, i) => (
              <Card key={i} className="bg-black border-gray-800 p-6 hover:border-emerald-800/50 transition-all text-white" data-testid={`vehicle-use-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{item.desc}</p>
                <ul className="space-y-2">
                  {item.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-gray-400">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Dealership Consulting */}
      <section className="py-20 border-t border-gray-900" data-testid="section-dealership-consulting">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Card className="bg-gray-950 border-gray-800 p-6 order-2 lg:order-1 text-white">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                <Handshake className="h-5 w-5 text-amber-500" /> What ACE Prepares For You
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'Market Price Report', desc: 'True market value based on comparable sales, not MSRP.' },
                  { label: 'Negotiation Playbook', desc: 'Your target price, walk-away price, and counter-offer strategy.' },
                  { label: 'Financing Pre-Check', desc: 'Credit review, pre-approval guidance, and rate comparison.' },
                  { label: 'Trade-In Strategy', desc: 'If applicable, we help you maximize your trade-in value.' },
                  { label: 'Add-On Protection', desc: 'What dealer add-ons are worth it, and which to decline.' },
                  { label: 'Post-Purchase Checklist', desc: 'Insurance, registration, maintenance schedule — covered.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-black rounded-lg border border-gray-800">
                    <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-bold">{item.label}</p>
                      <p className="text-[10px] text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Handshake className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-[10px] text-amber-500 font-bold tracking-widest uppercase">Before You Go</p>
                  <h2 className="text-2xl sm:text-3xl font-bold">Dealership Consulting</h2>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                Walking into a dealership unprepared costs the average buyer $3,000–$5,000. ACE levels the playing field before you set foot on the lot.
              </p>
              <p className="text-gray-400 leading-relaxed mb-6">
                We provide market pricing intelligence, negotiation strategy, financing pre-checks, and a clear target price — so you walk in with confidence and leave with the deal you deserve.
              </p>
              <div className="bg-emerald-950/30 border border-emerald-800/30 rounded-xl p-5">
                <p className="text-sm font-bold text-emerald-400 mb-2">ACE Consulting saves buyers an average of</p>
                <p className="text-4xl font-black text-emerald-400">$3,200</p>
                <p className="text-xs text-gray-500 mt-1">on dealership purchases vs. walking in cold</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lease to Own */}
      <section className="py-20 border-t border-gray-900 bg-gray-950/30" data-testid="section-lease-to-own">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-sky-500 text-xs font-bold tracking-[0.3em] uppercase mb-3">Ownership Path</p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Lease to Own</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">A structured path from renting to ownership. Build equity in a vehicle while you drive it for your business.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* How It Works */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Clock className="h-5 w-5 text-sky-500" /> How Lease to Own Works
              </h3>
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Vehicle Selection', desc: 'ACE sources the right vehicle based on your needs and budget. We handle auction access, dealer sourcing, or private party acquisition.' },
                  { step: '2', title: 'Down Payment', desc: 'You make an initial down payment (typically 10-20% of vehicle value). This reduces your monthly payments and builds immediate equity.' },
                  { step: '3', title: 'Monthly Payments', desc: 'Fixed monthly payments over 24-48 months. Every payment builds equity toward full ownership. Use the vehicle for business income while you pay.' },
                  { step: '4', title: 'Full Ownership', desc: 'At the end of the term, the vehicle is yours — free and clear. No balloon payments, no surprises.' },
                ].map((s, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-black rounded-xl border border-gray-800">
                    <div className="w-10 h-10 rounded-full bg-sky-600/20 flex items-center justify-center shrink-0">
                      <span className="text-sky-400 font-black text-sm">{s.step}</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold mb-1">{s.title}</p>
                      <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: DollarSign, label: 'Build Equity', desc: 'Every payment moves you closer to ownership' },
                  { icon: TrendingUp, label: 'Credit Building', desc: 'Consistent payments improve your credit profile' },
                  { icon: Car, label: 'Drive & Earn', desc: 'Use the vehicle for business while paying it off' },
                  { icon: Shield, label: 'No Balloon', desc: 'Transparent terms with no surprise final payment' },
                ].map((f, i) => (
                  <div key={i} className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <f.icon className="h-5 w-5 text-sky-500 mb-2" />
                    <p className="text-sm font-bold">{f.label}</p>
                    <p className="text-[10px] text-gray-500">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Calculator */}
            <Card className="bg-gray-950 border-sky-800/40 p-6 sticky top-24 text-white" data-testid="lease-calculator">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-sky-500" /> Payment Estimator
              </h3>
              <div className="space-y-5">
                <div>
                  <label className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-2 block">Vehicle Price</label>
                  <input type="range" min="10000" max="150000" step="1000" value={leaseCalc.price}
                    onChange={e => setLeaseCalc(p => ({ ...p, price: +e.target.value }))}
                    className="w-full accent-sky-500" data-testid="calc-price-slider" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$10k</span>
                    <span className="font-bold text-white text-sm">${leaseCalc.price.toLocaleString()}</span>
                    <span>$150k</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-2 block">Down Payment</label>
                  <input type="range" min="0" max={Math.round(leaseCalc.price * 0.5)} step="500" value={leaseCalc.down}
                    onChange={e => setLeaseCalc(p => ({ ...p, down: +e.target.value }))}
                    className="w-full accent-sky-500" data-testid="calc-down-slider" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$0</span>
                    <span className="font-bold text-white text-sm">${leaseCalc.down.toLocaleString()} ({Math.round(leaseCalc.down / leaseCalc.price * 100)}%)</span>
                    <span>${Math.round(leaseCalc.price * 0.5 / 1000)}k</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-2 block">Term (Months)</label>
                  <div className="flex gap-2">
                    {[24, 36, 48].map(m => (
                      <button key={m} onClick={() => setLeaseCalc(p => ({ ...p, months: m }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${leaseCalc.months === m ? 'bg-sky-600 text-white' : 'bg-gray-900 text-gray-400 hover:bg-gray-800'}`}
                        data-testid={`calc-months-${m}`}>
                        {m} mo
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-black rounded-xl p-5 border border-gray-800 text-center" data-testid="calc-result">
                  <p className="text-[10px] text-gray-600 font-bold uppercase tracking-wider mb-1">Estimated Monthly Payment</p>
                  <p className="text-4xl font-black text-sky-400">${monthlyPayment.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-2">/month for {leaseCalc.months} months at {leaseCalc.rate}% APR</p>
                  <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-800">
                    <div>
                      <p className="text-[10px] text-gray-600 uppercase">Total Cost</p>
                      <p className="text-sm font-bold">${(monthlyPayment * leaseCalc.months + leaseCalc.down).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-600 uppercase">Financed</p>
                      <p className="text-sm font-bold">${(leaseCalc.price - leaseCalc.down).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <p className="text-[10px] text-gray-600 text-center">Estimates are for illustration only. Actual terms may vary based on credit, vehicle, and market conditions.</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 border-t border-gray-900" data-testid="section-faq">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-emerald-500 text-xs font-bold tracking-[0.3em] uppercase mb-3">Questions</p>
            <h2 className="text-2xl sm:text-3xl font-bold">Frequently Asked</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-gray-800 rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-950 transition-colors">
                  <span className="text-sm font-bold pr-4">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="h-4 w-4 text-gray-500 shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-gray-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 border-t border-gray-900 text-center">
        <div className="max-w-lg mx-auto px-6">
          <Car className="h-10 w-10 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-3">Ready to Find Your Next Vehicle?</h3>
          <p className="text-sm text-gray-500 mb-8">Whether you're buying at auction, negotiating at a dealership, or building toward ownership — ACE is your concierge through every step.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/consultation/intake')} className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8 text-sm font-bold" data-testid="sales-cta-bottom">
              Start Your Vehicle Search <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-12 px-6 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold transition-colors">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesInfo;
